/*
  FileToHash.jsx
  ----------------
  Interface pour récupérer la signature d'un document.
*/
import { useState } from 'react';
import { Box, Text, Button, Stack, TextInput, Loader, Center, Group, rem, Notification } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconUpload, IconX, IconFile, IconCopy } from '@tabler/icons-react';
import { uploadFile } from '../tools/Request';

export default function FileToHash() {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [hash, setHash] = useState('');
  const [error, setError] = useState('');
  const handleDrop = (acceptedFiles) => {
    setFiles(acceptedFiles);
    setError('');
    setHash('');
  };

  const handleSubmitFields = async () => {
    if (!files.length) {
      setError('Veuillez déposer un fichier avant de continuer.');
      return;
    }

    setLoading(true);
    setError('');
    setHash('');

    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const response = await uploadFile('tools/hashfromfile', formData, localStorage.getItem('userToken'));
      const { hash } = response;
      setHash(hash);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(hash);
  };

  return (
    <Center>
      <Box
        mt="5%"
        backgroundColor="white"
        maw={800}
        style={{
          flex: 1,
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
        <Text style={{ fontSize: '1.25rem', fontWeight: 500, marginBottom: '1rem' }}>Récupérer la signature d'un document</Text>
        <Stack>
          <Dropzone
            onDrop={handleDrop}
            onReject={(rejectedFiles) => setError('Fichier rejeté. Vérifiez sa taille et son type.')}
            maxSize={5 * 1024 ** 2} // 5MB max file size
          >
            <Group justify="center" gap="sm" mih={20} style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <IconUpload
                  style={{
                    width: rem(40),
                    height: rem(40),
                    color: 'var(--mantine-color-blue-6)'
                  }}
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  style={{
                    width: rem(40),
                    height: rem(40),
                    color: 'var(--mantine-color-red-6)'
                  }}
                  stroke={1.5}
                />
              </Dropzone.Reject>
              {!files.length && (
                <Dropzone.Idle>
                  <IconFile
                    style={{
                      width: rem(40),
                      height: rem(40),
                      color: 'var(--mantine-color-dimmed)'
                    }}
                    stroke={1.5}
                  />
                </Dropzone.Idle>
              )}
              <div>
                <Text size="md" inline>
                  {files.length ? files[0].name : 'Déposer votre fichier ici'}
                </Text>
              </div>
            </Group>
          </Dropzone>

          {error && (
            <Notification color="red" title="Erreur" onClose={() => setError('')}>
              {error}
            </Notification>
          )}

          <Button color="teal" onClick={handleSubmitFields} disabled={loading}>
            {loading ? <Loader size="xs" /> : 'Récupérer la signature'}
          </Button>

          {hash && (
            <Box mt="1rem">
              <Text>Signature</Text>
              <Group>
                <TextInput value={hash} readOnly style={{ flex: 1 }} />
                <Button color="blue" onClick={copyToClipboard}>
                  <IconCopy />
                </Button>
              </Group>
            </Box>
          )}
        </Stack>
      </Box>
    </Center>
  );
}
